import { useState } from "react";
import ReactECharts from "echarts-for-react";

const CHART_LABELS = ["S1", "S2", "S3", "S4", "S5", "S6", "S7", "S8", "P1", "P2", "P3"];
const ACTUAL = [39, 44, 47, 51, 56, 61, 68, 74, null, null, null];
const PREDICTED = [null, null, null, null, null, null, null, 74, 77, 79, 81];
const AVERAGE = [42, 45, 46, 48, 50, 54, 57, 63, 66, 68, 70];
const LOWER_BAND = [34, 37, 39, 42, 44, 47, 50, 56, 58, 60, 62];
const UPPER_BAND = [50, 53, 55, 57, 60, 63, 66, 70, 73, 75, 77];

interface Scenario {
  key: string;
  tab: string;
  title: string;
  copy: string;
  bullets: string[];
}

const SCENARIOS: Scenario[] = [
  {
    key: "crosswind",
    tab: "Crosswind recovery",
    title: "Crosswind recovery block",
    copy: "Run 4 short circuits with asymmetric wind bursts and recover without altitude overshoot above 8 meters.",
    bullets: [
      "Target: reduce yaw overcorrection after gate 3.",
      "Watch item: nose drift during throttle recovery.",
      "Status: recommended by mocked AI coach.",
    ],
  },
  {
    key: "urban",
    tab: "Urban canyon",
    title: "Urban canyon threading",
    copy: "Practice slow-speed directional control through narrow corridors with delayed line-of-sight changes.",
    bullets: [
      "Target: maintain smoother turn entry under reduced visibility.",
      "Watch item: oscillation after obstacle exit.",
      "Status: suggested for the next week of training.",
    ],
  },
  {
    key: "landing",
    tab: "Precision landing",
    title: "Precision landing ladder",
    copy: "Stack 6 landing attempts on progressively smaller pads while preserving low lateral drift on final approach.",
    bullets: [
      "Target: hold descent rate inside the green band for 80% of final approach.",
      "Watch item: roll correction after touchdown flare.",
      "Status: fallback drill if crosswind block is completed early.",
    ],
  },
];

function useRunVbs() {
  const [status, setStatus] = useState(
    "Mock action only. No VBS integration is connected.",
  );
  const [busy, setBusy] = useState(false);

  function run() {
    setBusy(true);
    setStatus("Mock VBS launch in progress...");
    window.setTimeout(() => {
      setBusy(false);
      setStatus("Mock complete. No real VBS process was started.");
    }, 1500);
  }

  return { status, busy, run };
}

function buildCoachOption() {
  const body = document.body ? getComputedStyle(document.body) : null;
  const textColor = body?.getPropertyValue("--text").trim() || "#E8ECF1";
  const textMuted = body?.getPropertyValue("--text-muted").trim() || "#A0AAB8";
  const borderColor = body?.getPropertyValue("--border").trim() || "rgba(42,52,68,0.5)";
  const isLight = document.documentElement.dataset.uiTheme === "light";
  const tooltipBg = isLight ? "rgba(255, 253, 249, 0.96)" : "rgba(10, 14, 20, 0.96)";
  const areaBand = isLight ? "rgba(160, 170, 184, 0.16)" : "rgba(61, 74, 94, 0.18)";
  const actualPointFill = isLight ? "#FFFDF9" : "#0A0E14";
  const actualAreaStart = isLight ? "rgba(255, 107, 26, 0.14)" : "rgba(255, 107, 26, 0.22)";
  const markAreaColor = isLight ? "rgba(255, 107, 26, 0.08)" : "rgba(255, 107, 26, 0.06)";

  return {
    animationDuration: 900,
    grid: { top: 28, right: 20, bottom: 30, left: 38 },
    legend: {
      data: ["Average pilot", "Actual experience", "Predicted progress"],
      top: 0,
      right: 0,
      itemWidth: 10,
      itemHeight: 10,
      icon: "circle",
      textStyle: { color: textMuted, fontSize: 11 },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: tooltipBg,
      borderColor,
      borderWidth: 1,
      textStyle: { color: textColor },
    },
    xAxis: {
      type: "category",
      data: CHART_LABELS,
      axisLine: { lineStyle: { color: borderColor } },
      axisTick: { show: false },
      axisLabel: { color: textMuted },
    },
    yAxis: {
      type: "value",
      min: 20,
      max: 90,
      splitNumber: 7,
      axisLabel: { color: textMuted },
      splitLine: { lineStyle: { color: borderColor, type: "dashed" } },
    },
    series: [
      {
        name: "Mocked lower band",
        type: "line",
        data: LOWER_BAND,
        lineStyle: { opacity: 0 },
        symbol: "none",
        stack: "band",
      },
      {
        name: "Average band",
        type: "line",
        data: UPPER_BAND.map((value, index) => value - LOWER_BAND[index]),
        lineStyle: { opacity: 0 },
        symbol: "none",
        stack: "band",
        areaStyle: { color: areaBand },
      },
      {
        name: "Average pilot",
        type: "line",
        data: AVERAGE,
        smooth: 0.25,
        symbol: "none",
        lineStyle: { width: 2, color: "#A0AAB8", type: "dashed" },
      },
      {
        name: "Actual experience",
        type: "line",
        data: ACTUAL,
        smooth: 0.22,
        symbol: "circle",
        symbolSize: 8,
        lineStyle: { width: 4, color: "#FF6B1A" },
        itemStyle: { color: actualPointFill, borderColor: "#FF6B1A", borderWidth: 2 },
        areaStyle: {
          color: actualAreaStart,
        },
      },
      {
        name: "Predicted progress",
        type: "line",
        data: PREDICTED,
        smooth: 0.3,
        symbol: "diamond",
        symbolSize: 9,
        lineStyle: { width: 3, color: "#4ADE80", type: "dashed" },
        itemStyle: { color: "#4ADE80", borderColor: "#C8F2D7", borderWidth: 1 },
        markArea: {
          silent: true,
          itemStyle: { color: markAreaColor },
          data: [[{ xAxis: "P1" }, { xAxis: "P3" }]],
        },
      },
    ],
  };
}

export function AiCoachPage() {
  const { status, busy, run } = useRunVbs();
  const [activeTab, setActiveTab] = useState("crosswind");
  const activeScenario = SCENARIOS.find((s) => s.key === activeTab) ?? SCENARIOS[0];

  return (
    <div className="mock-page" id="ai-coach-page">
      <section className="mock-hero">
        <div className="mock-hero-copy">
          <div className="mock-badges">
            <span className="mock-badge mock-badge-hot">MOCKUP</span>
            <span className="mock-badge">SIMULATED AI OUTPUT</span>
          </div>
          <p className="eyebrow">Training intelligence</p>
          <h1>AI Coach</h1>
          <p className="mock-lede">
            Mocked coaching surface showing a simulated development curve for drone-control experience,
            predicted progress, benchmark position, and suggested training focus.
          </p>
        </div>
        <div className="mock-hero-actions">
          <button
            className="button primary mock-run-button"
            type="button"
            onClick={run}
            disabled={busy}
          >
            {busy ? "Run VBS (mock loading...)" : "Run VBS"}
          </button>
          <span className="mock-run-status">{status}</span>
        </div>
      </section>

      <section className="mock-warning-strip">
        <strong>Mock data only.</strong>
        <span>Percentiles, predictions, and recommendations below are hard-coded UI placeholders.</span>
      </section>

      <section className="mock-stat-grid">
        <article className="mock-stat-card">
          <span>Current experience index</span>
          <strong>74 / 100</strong>
          <small>Simulated control maturity for the last 8 sessions</small>
        </article>
        <article className="mock-stat-card mock-stat-positive">
          <span>Average benchmark</span>
          <strong>Above average</strong>
          <small>+11 points versus the mocked cohort baseline</small>
        </article>
        <article className="mock-stat-card mock-stat-positive">
          <span>Model expectation</span>
          <strong>Better than predicted</strong>
          <small>Current trend is 6% stronger than the simulated model forecast</small>
        </article>
        <article className="mock-stat-card">
          <span>Next 3-flight projection</span>
          <strong>81 / 100</strong>
          <small>Predicted confidence band shown in the chart below</small>
        </article>
      </section>

      <section className="mock-grid mock-grid-coach">
        <article className="panel mock-panel mock-watermarked">
          <div className="panel-head">
            <h2>Experience development and prediction</h2>
            <p>Historic simulated score, average pilot band, and mocked progression forecast</p>
          </div>
          <div className="mock-chart-stage">
            <ReactECharts
              option={buildCoachOption()}
              notMerge
              style={{ height: "100%", width: "100%" }}
            />
          </div>
          <div className="mock-panel-note">
            <span className="mock-dot mock-dot-live" />
            Prediction line is generated from fixed mock values for the prototype.
          </div>
        </article>

        <aside className="mock-stack">
          <article className="panel mock-panel mock-watermarked">
            <div className="panel-head">
              <h2>Model readout</h2>
              <p>Simulated interpretation of the current pilot profile</p>
            </div>
            <div className="mock-kv-list">
              <div>
                <span>Pilot percentile</span>
                <strong>78th percentile</strong>
              </div>
              <div>
                <span>Confidence</span>
                <strong>0.82 mocked certainty</strong>
              </div>
              <div>
                <span>Primary strength</span>
                <strong>Stable throttle discipline</strong>
              </div>
              <div>
                <span>Main weakness</span>
                <strong>Late yaw correction in precision turns</strong>
              </div>
            </div>
          </article>

          <article className="panel mock-panel mock-watermarked">
            <div className="panel-head">
              <h2>Recommendation</h2>
              <p>Short mocked coaching advice</p>
            </div>
            <p className="mock-recommendation-copy">
              Train low-altitude figure-eight runs with delayed crosswind gusts. Focus on earlier yaw input,
              then stabilize with smaller corrective roll pulses instead of aggressive catch-up turns.
            </p>
          </article>
        </aside>
      </section>

      <section className="panel mock-panel mock-watermarked">
        <div className="panel-head">
          <h2>Training scenarios</h2>
          <p>Prototype tab set with mocked drill recommendations</p>
        </div>
        <div className="mock-tabs">
          <div className="mock-tab-list" role="tablist" aria-label="Training scenarios">
            {SCENARIOS.map((scenario) => (
              <button
                key={scenario.key}
                className={`mock-tab${activeTab === scenario.key ? " is-active" : ""}`}
                type="button"
                role="tab"
                onClick={() => setActiveTab(scenario.key)}
              >
                {scenario.tab}
              </button>
            ))}
          </div>
          <div className="mock-tab-panels">
            <article className="mock-tab-panel is-active">
              <h3>{activeScenario.title}</h3>
              <p>{activeScenario.copy}</p>
              <ul className="mock-bullet-list">
                {activeScenario.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
}
